import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import { refreshCrew } from '../crew/refreshCrew';
import { DialogLayer } from '../ui/Dialog';
import { releasedText, releaseQuestion, shortDate, type GridAssignment } from './peopleGrid';

type Released = { released: Array<{ day: string; personName?: string }>; removed: boolean };

/**
 * Owner 2026-10-26 07:42: „Usuń ze stanowiska” from the cell menu — asked first; on a multi-day event „Tylko ten dzień”
 * or „Wszystkie dni wydarzenia” (POST /api/crew/assignments/:id/release). The place stays free in the event's Obsada;
 * the events strip shows the gap again (refreshCrew).
 */
export function ReleaseDialog({ personName, day, assignment, onDone, onClose }: { personName: string; day: string; assignment: GridAssignment; onDone: (text: string) => void; onClose: () => void }) {
  const queryClient = useQueryClient();
  const release = useMutation({
    mutationFn: (allDays: boolean) => api<Released>(`/api/crew/assignments/${assignment.assignmentId!}/release`, { method: 'POST', body: allDays ? { allDays: true } : { day } }),
    onSuccess: async (r) => {
      onDone(releasedText(personName, r.released.map((x) => x.day)));
      onClose();
      await refreshCrew(queryClient);
    },
  });
  return (
    <div className="dialog-backdrop">
      <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="pg-release-title" data-feature-id="crew.people_availability">
        <DialogLayer onClose={onClose} busy={release.isPending} />
        <h2 id="pg-release-title">Usunąć ze stanowiska?</h2>
        <p>{releaseQuestion(personName, assignment, day)}</p>
        <p className="muted">Miejsce zostanie wolne w obsadzie wydarzenia, a pasek wydarzenia znów pokaże brak.</p>
        {release.error && (
          <p className="alert" role="alert">
            {release.error instanceof ApiError ? release.error.message : 'Nie udało się usunąć'}
          </p>
        )}
        <div className="actions">
          {assignment.multiDay ? (
            <>
              <button className="btn primary" type="button" disabled={release.isPending} onClick={() => release.mutate(false)}>
                Tylko {shortDate(day)}
              </button>
              <button className="btn" type="button" disabled={release.isPending} onClick={() => release.mutate(true)}>
                Wszystkie dni wydarzenia
              </button>
            </>
          ) : (
            <button className="btn primary" type="button" disabled={release.isPending} onClick={() => release.mutate(false)}>
              {release.isPending ? 'Usuwanie…' : 'Usuń ze stanowiska'}
            </button>
          )}
          <button className="btn" type="button" disabled={release.isPending} onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}
